import { useEffect, useState } from "react";
/**
 * The browser default font size in pixels. WCAG 1.4.4 "Resize Text" tests
 * (e.g. the 200% bookmarklet) set `document.documentElement.style.fontSize`
 * to a percentage relative to this value.
 */
const DEFAULT_BROWSER_FONT_SIZE_PX = 16;
function getRootFontSizePx() {
    if (typeof window === "undefined")
        return DEFAULT_BROWSER_FONT_SIZE_PX;
    const computed = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return isNaN(computed) ? DEFAULT_BROWSER_FONT_SIZE_PX : computed;
}
function computeState() {
    const zoomFactor = getRootFontSizePx() / DEFAULT_BROWSER_FONT_SIZE_PX;
    const effectiveViewportWidth = typeof window !== "undefined" ? window.innerWidth / zoomFactor : 0;
    return { zoomFactor, effectiveViewportWidth };
}
/**
 * Tracks WCAG 1.4.4 "text-size" zoom applied via `<html>` `font-size`
 * changes (e.g. browser accessibility tools or the 200% bookmarklet).
 *
 * Returns:
 * - `zoomFactor` — ratio of current root font-size to the browser default
 *   (16 px). At 200% text size this is `2`.
 * - `effectiveViewportWidth` — `window.innerWidth / zoomFactor`, i.e. the
 *   logical viewport width as perceived by the zoomed-in user. Use this
 *   instead of raw `window.innerWidth` for responsive breakpoint decisions.
 *
 * ---
 *
 * ## Testing with the 200% text-size bookmarklet
 *
 * Browser zoom and text-size zoom are not the same thing. WCAG 2.2 SC 1.4.4
 * requires that text can be resized up to 200% **without** browser zoom and
 * without loss of content or functionality. Use the bookmarklet below to
 * toggle a `font-size: 200%` override on `<html>` with a single click.
 *
 * To add the bookmarklet to your browser:
 *
 * 1. Create a new bookmark (any page).
 * 2. Give it a name such as **"Toggle text zoom"**.
 * 3. Paste the following as the bookmark URL:
 *
 * ```
 * javascript: (function () {
 *   const htmlElement = document.querySelector("html");
 *   const currentFontSize = htmlElement.style.getPropertyValue("font-size");
 *   const isZoomed = currentFontSize === "200%";
 *   const newFontSize = isZoomed ? null : "200%";
 *   htmlElement.style.setProperty("font-size", newFontSize);
 * })();
 * ```
 *
 * Click the bookmark on any page to toggle 200% text-size on/off.
 *
 * > **Note:** if the page already sets `font-size` on `<html>` via CSS, the
 * > bookmarklet may not work as intended.
 *
 * @see https://ashleemboyer.com/blog/an-accessibility-bookmarklet-for-testing-200-percent-text-size
 */
export function useRootFontSizeZoom() {
    const [state, setState] = useState(computeState);
    useEffect(() => {
        function update() {
            setState((prev) => {
                const next = computeState();
                // Bail out of the render if nothing meaningful changed.
                if (prev.zoomFactor === next.zoomFactor &&
                    prev.effectiveViewportWidth === next.effectiveViewportWidth) {
                    return prev; // same reference → React skips re-render
                }
                return next;
            });
        }
        // Watch for inline style / class changes on <html> that carry a new
        // font-size (e.g. set by the WCAG 200% bookmarklet).
        // This fires rarely, so no debounce needed.
        const observer = new MutationObserver(update);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["style", "class"],
        });
        // Debounce resize — it fires at 60+ fps during a drag and
        // effectiveViewportWidth changes on every pixel.
        let resizeTimer;
        function handleResize() {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(update, 150);
        }
        window.addEventListener("resize", handleResize);
        return () => {
            observer.disconnect();
            clearTimeout(resizeTimer);
            window.removeEventListener("resize", handleResize);
        };
    }, []);
    return state;
}
//# sourceMappingURL=useRootFontSizeZoom.js.map